/**
 * RotationAutoTest — analog zu HighlightAutoTest, aber für Pre-OCR-Rotation.
 * Ruft POST /api/admin/test-protocols/run-rotation/:card_id (Tesseract OSD)
 * und beantwortet eine binäre Frage: "Ist die Seite gerade, oder muss sie
 * gedreht werden bevor OCR sinnvoll läuft?"
 *
 * Eingebettet in Phase 1 Step 3 ("Manuelle Orientierungsklärung nötig").
 * Kind-agnostisch — funktioniert für Stundenlisten + Diäten.
 *
 * Verdict-Familie:
 *   ROTATION_OK         · ✅ Seite gerade, OCR kann starten
 *   NEEDS_ROTATION      · ⚠ Drehung erkannt — Worker oder User wendet sie an
 *   LOW_CONFIDENCE      · ❓ OSD unsicher → User entscheidet
 *   OSD_ERROR           · ❌ Tesseract scheiterte (Render/Layout-Problem)
 *   PDF_FILE_MISSING    · ❌ pdf_path leer oder File nicht lesbar
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type RunRotationForCardResponse, type RotationVerdict } from "../../lib/api";

type Phase = "idle" | "loading" | "running" | "done" | "error";

interface Tone {
  bg: string; fg: string; border: string; icon: string;
  label: string;
}

const TONE: Record<RotationVerdict, Tone> = {
  ROTATION_OK:       { bg: "#ecfdf5", fg: "#065f46", border: "#86efac", icon: "✅", label: "Seite gerade — OCR kann starten" },
  NEEDS_ROTATION:    { bg: "#fef3c7", fg: "#92400e", border: "#fcd34d", icon: "↻",  label: "Drehung erkannt" },
  LOW_CONFIDENCE:    { bg: "#f3f4f6", fg: "#374151", border: "#d1d5db", icon: "❓", label: "OSD unsicher — User entscheidet" },
  OSD_ERROR:         { bg: "#fef2f2", fg: "#991b1b", border: "#fca5a5", icon: "❌", label: "OSD-Fehler" },
  PDF_FILE_MISSING:  { bg: "#fef2f2", fg: "#991b1b", border: "#fca5a5", icon: "❌", label: "PDF nicht lesbar" },
};

function reasonText(r: RunRotationForCardResponse): string {
  const conf = `confidence=${r.confidence.toFixed(2)} (raw=${r.raw_conf.toFixed(2)}, Schwelle ${r.confidence_threshold.toFixed(2)})`;
  const script = r.script ? `, script=${r.script}` : "";
  switch (r.verdict) {
    case "ROTATION_OK":
      return `OSD sagt 0° mit ausreichender Sicherheit — ${conf}${script}`;
    case "NEEDS_ROTATION":
      return `OSD erkennt ${r.rotate}° Drehung — ${conf}${script}`;
    case "LOW_CONFIDENCE":
      return `OSD-Vorschlag ${r.rotate}°, aber zu unsicher — ${conf}${script}`;
    case "OSD_ERROR":
      return `Tesseract konnte OSD nicht ausführen: ${r.error ?? "unbekannt"}`;
    case "PDF_FILE_MISSING":
      return `PDF-Datei nicht lesbar: ${r.error ?? "unbekannt"}`;
  }
}

export function RotationAutoTest({ cardId }: { cardId: string }) {
  const [phase, setPhase] = useState<Phase>("loading");
  const [result, setResult] = useState<RunRotationForCardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setPhase("loading");
    api.testProtocols.getLatestRotationForCard(cardId)
      .then((r) => {
        if (cancelled) return;
        if (r) { setResult(r); setPhase("done"); }
        else { setPhase("idle"); }
      })
      .catch(() => { if (!cancelled) setPhase("idle"); });
    return () => { cancelled = true; };
  }, [cardId]);

  async function run() {
    setPhase("running");
    setError(null);
    try {
      const r = await api.testProtocols.runRotationForCard(cardId);
      setResult(r);
      setPhase("done");
    } catch (e: any) {
      setError(e?.message ?? String(e));
      setPhase("error");
    }
  }

  async function copyRunId() {
    if (!result?.id) return;
    await navigator.clipboard.writeText(result.id);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <button
        type="button"
        onClick={run}
        disabled={phase === "running" || phase === "loading"}
        style={{
          padding: "5px 12px", fontSize: 12, fontWeight: 600,
          background: phase === "running" || phase === "loading" ? "#9ca3af" : "#1d4ed8",
          color: "white", border: 0, borderRadius: 4,
          cursor: phase === "running" || phase === "loading" ? "wait" : "pointer",
          alignSelf: "flex-start",
        }}
        title="Startet Tesseract OSD auf der Original-PDF (~1–3 s). Ergebnis persistiert in test_run_protocols."
      >
        {phase === "loading"
          ? "lade letzten Lauf …"
          : phase === "running"
            ? "läuft …"
            : phase === "done"
              ? "🔁 Rotation erneut prüfen"
              : "Rotation automatisch prüfen"}
      </button>

      {phase === "error" && error && (
        <div style={{ color: "#991b1b", fontSize: 13 }}>Fehler: {error}</div>
      )}

      {phase === "done" && result && (
        <RotationResultPanel result={result} onCopy={copyRunId} copied={copied} />
      )}
    </div>
  );
}

function RotationResultPanel({
  result, onCopy, copied,
}: {
  result: RunRotationForCardResponse;
  onCopy: () => void;
  copied: boolean;
}) {
  const tone = TONE[result.verdict];
  return (
    <div style={{
      background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
      padding: 10, borderRadius: 4, fontSize: 13,
      display: "flex", flexDirection: "column", gap: 6,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
        <strong>{tone.icon} {result.verdict} — {tone.label}</strong>
        <span
          style={{ fontFamily: "monospace", fontSize: 12, opacity: 0.8, cursor: "pointer" }}
          onClick={onCopy}
          title="Run-ID in Clipboard kopieren"
        >
          Run-ID: {result.id.slice(0, 8)}… {copied ? "✓ kopiert" : "📋"}
        </span>
      </div>
      {result.run_at && (
        <div style={{ fontSize: 11, opacity: 0.7 }}>
          Lauf vom {new Date(result.run_at).toLocaleString("de-AT")}
        </div>
      )}
      <div>{reasonText(result)}</div>
      <Link to={`/admin/test-protocols/${result.id}`} style={{ fontSize: 12, color: tone.fg }}>
        Voll-Detail (JSON) →
      </Link>
    </div>
  );
}
